import { useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes } from 'react';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-white border-brand hover:bg-brand-dark font-semibold',
  secondary: 'bg-white text-ink border-[#c9ced8] hover:bg-chip',
  danger: 'bg-white text-bad border-bad/40 hover:bg-red-50',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-chip',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg border whitespace-nowrap transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
        size === 'sm' && 'px-3 py-1.5 text-sm',
        size === 'md' && 'px-4 py-2.5',
        size === 'lg' && 'px-5 py-3.5 text-lg',
        VARIANT[variant],
        className,
      )}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cx('inline-block animate-spin rounded-full border-2 border-current border-r-transparent', className ?? 'size-5')} aria-hidden />;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-muted">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-sm text-bad">{error}</span> : hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

const inputCls = 'w-full rounded-lg border border-[#c9ced8] bg-white px-3 py-2.5 text-ink placeholder:text-muted/70 focus:border-brand focus:outline-2 focus:outline-brand disabled:bg-chip';

// React 19: `ref` is a regular prop on function components.
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input {...rest} className={cx(inputCls, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(inputCls, 'pr-8', className)}>
      {children}
    </select>
  );
}

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-xl border border-line bg-white p-4', className)}>
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {title && <h2 className="mr-auto text-lg font-semibold">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <div className="mr-auto">
        <h1 className="text-2xl font-bold">{title}</h1>
        {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'brand';
const TONE: Record<Tone, string> = {
  neutral: 'bg-chip text-muted',
  ok: 'bg-green-50 text-ok',
  warn: 'bg-amber-50 text-warn',
  bad: 'bg-red-50 text-bad',
  brand: 'bg-brand text-white',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx('inline-block rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', TONE[tone])}>{children}</span>;
}

export function Alert({ tone = 'bad', children }: { tone?: Exclude<Tone, 'brand' | 'neutral'> | 'info'; children: ReactNode }) {
  const cls = tone === 'info' ? 'bg-chip text-ink' : TONE[tone];
  return <div className={cx('rounded-lg px-3 py-2.5 text-sm', cls)} role={tone === 'bad' ? 'alert' : 'status'}>{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed border-line px-4 py-8 text-center text-muted">{children}</div>;
}

export function Table({ head, children, minWidth = 560 }: { head: ReactNode[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <table className="w-full border-collapse text-sm" style={{ minWidth }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} className="border-b border-line px-2 py-2 text-left text-xs font-semibold text-muted">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-line [&_td]:px-2 [&_td]:py-2 [&_td]:align-top">{children}</tbody>
      </table>
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return <p className="mt-3 text-sm text-muted">ทั้งหมด {total.toLocaleString()} รายการ</p>;
  return (
    <div className="mt-3 flex items-center gap-2 text-sm">
      <span className="mr-auto text-muted">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} จาก {total.toLocaleString()}
      </span>
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ก่อนหน้า
      </Button>
      <span>
        {page}/{pages}
      </span>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        ถัดไป
      </Button>
    </div>
  );
}

export function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  // The <dialog> "close" event fires asynchronously. When the parent closes the modal by setting
  // open=false, calling onClose again later would run its side effects at the wrong moment.
  const closedByParent = useRef(false);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      closedByParent.current = false;
      d.showModal();
      // showModal() focuses the first focusable element (the ✕ button), which would swallow a
      // scanner's Enter. Move focus to the first editable field instead.
      const target = d.querySelector<HTMLElement>(
        '[data-autofocus], input:not([readonly]):not([type=hidden]):not([type=checkbox]):not([type=radio]), select, textarea',
      );
      target?.focus();
    }
    if (!open && d.open) {
      closedByParent.current = true;
      d.close();
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={() => {
        if (closedByParent.current) closedByParent.current = false;
        else onClose(); // Esc key or the ✕ button
      }}
      className="m-auto w-[min(560px,calc(100vw-24px))] rounded-xl p-0 backdrop:bg-black/45"
    >
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <h3 className="mr-auto font-semibold">{title}</h3>
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="ปิด">
          ✕
        </Button>
      </div>
      <div className="max-h-[calc(100vh-140px)] overflow-auto p-4">{open && children}</div>
    </dialog>
  );
}

/** Copyable one-time secret (temporary passwords). */
export function SecretBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="text-sm text-warn">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="grow rounded bg-white px-2 py-1 font-mono text-lg tracking-wider select-all">{value}</code>
        <Button size="sm" onClick={() => navigator.clipboard?.writeText(value)}>
          คัดลอก
        </Button>
      </div>
      <div className="mt-1 text-xs text-muted">แสดงครั้งเดียว — ผู้ใช้ต้องเปลี่ยนรหัสผ่านเมื่อเข้าสู่ระบบครั้งแรก</div>
    </div>
  );
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
